import React, { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { ReactSketchCanvas, ReactSketchCanvasRef } from "react-sketch-canvas";
import { Typography, Button } from "@mui/material";
import { t } from "i18next";
import UndoIcon from "@mui/icons-material/Undo";
import RedoIcon from "@mui/icons-material/Redo";
import DeleteIcon from "@mui/icons-material/Delete";
import "./PicassoCanvas.css";
import useUiScale from "../../shared/helper/useUiScale";

const COLORS = [
  "#e53935", "#fb8c00", "#fdd835", "#43a047",
  "#00acc1", "#1e88e5", "#8e24aa", "#ec407a",
  "#FFCBA4", "#6d4c41", "#000000", "#757575",
  "#BDBDBD", "#FFFFFF",
];

const BRUSH_SIZES = [3, 6, 12, 20];

export interface PicassoCanvasHandle {
  forceExport: () => void;
}

interface PicassoCanvasProps {
  onExport: (svg: string) => void;
  onUnsubmit?: () => void;
  imposterTarget?: string;
  disabled?: boolean;
  onImposterDecision?: (accepted: boolean) => void;
  submittedCount?: number;
  totalPlayers?: number;
}

const PicassoCanvas = forwardRef<PicassoCanvasHandle, PicassoCanvasProps>(
function PicassoCanvas({ onExport, onUnsubmit, imposterTarget, disabled, onImposterDecision, submittedCount, totalPlayers }: PicassoCanvasProps, ref) {
  const canvasRef = useRef<ReactSketchCanvasRef>(null);
  // A real, larger drawing surface on big displays — the canvas keeps mapping pointer position to
  // its own pixels one to one, so drawing stays exact.
  const uiScale = useUiScale();
  const canvasPx = Math.round(300 * uiScale);
  const [strokeColor, setStrokeColor] = useState("#000000");
  const [strokeWidth, setStrokeWidth] = useState(6);
  const [eraseMode, setEraseMode] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);
  const [imposterDecision, setImposterDecision] = useState<boolean | null>(null);

  const pendingDecision = imposterTarget != null && imposterDecision === null;
  const canvasDisabled = hasSubmitted || pendingDecision;

  const handleExport = async () => {
    if (!canvasRef.current || hasSubmitted) return;
    try {
      // Wait for React to flush any pending state updates and re-render paths into the SVG DOM.
      // Two rAFs: first waits for the current frame to finish, second ensures React has painted.
      await new Promise<void>(resolve => requestAnimationFrame(() => requestAnimationFrame(() => resolve())));
      // Additional small delay to handle slow devices / React batching
      await new Promise<void>(resolve => setTimeout(resolve, 50));
      const paths = await canvasRef.current.exportPaths();
      if (!paths || paths.length === 0) return; // don't submit empty canvas
      const svg = await canvasRef.current.exportSvg();
      setHasSubmitted(true);
      onExport(svg);
    } catch (e) {
      console.error("Failed to export SVG", e);
    }
  };

  useImperativeHandle(ref, () => ({ forceExport: handleExport }));

  const handleStroke = (_path: any, _isEraser: boolean) => {
    if (!hasDrawn) setHasDrawn(true);
  };

  const toggleEraser = () => {
    const newMode = !eraseMode;
    setEraseMode(newMode);
    canvasRef.current?.eraseMode(newMode);
  };

  return (
    <div className="picasso-canvas-container">
      <div className={`picasso-canvas-layout ${hasSubmitted ? "picasso-submitted" : ""}`}>
        {/* Color palette — left side on desktop */}
        <div className="picasso-colors-panel">
          {COLORS.map((color) => (
            <button
              key={color}
              className={`picasso-color-btn ${strokeColor === color && !eraseMode ? "active" : ""}`}
              style={{ backgroundColor: color }}
              onClick={() => { if (canvasDisabled) return; setStrokeColor(color); if (eraseMode) toggleEraser(); }}
              disabled={canvasDisabled}
            />
          ))}
        </div>

        <div className="picasso-canvas-area">
          <div className={`picasso-canvas-wrapper ${pendingDecision ? "picasso-canvas-dimmed" : ""}`}>
            <ReactSketchCanvas
              ref={canvasRef}
              width={`${canvasPx}px`}
              height={`${canvasPx}px`}
              canvasColor="#FFFFFF"
              strokeColor={strokeColor}
              strokeWidth={strokeWidth}
              eraserWidth={strokeWidth * 2}
              onStroke={handleStroke}
              style={{ border: "none", pointerEvents: canvasDisabled ? "none" : "auto" }}
            />
          </div>
          {pendingDecision && (
            <div className="picasso-imposter-overlay">
              <div className="picasso-imposter-banner">
                <span>{t("picasso_impersonate_prompt")} <strong>{imposterTarget}</strong>?</span>
                <div className="picasso-imposter-decision-buttons">
                  <button
                    className="picasso-imposter-accept-btn"
                    onClick={() => { setImposterDecision(true); onImposterDecision?.(true); }}
                  >
                    {t("accept")}
                  </button>
                  <button
                    className="picasso-imposter-decline-btn"
                    onClick={() => { setImposterDecision(false); onImposterDecision?.(false); }}
                  >
                    {t("decline")}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Actions — right side on desktop */}
        <div className="picasso-actions-panel">
          <div className="picasso-actions-grid">
            {BRUSH_SIZES.map((size) => (
              <button
                key={size}
                className={`picasso-brush-btn ${strokeWidth === size ? "active" : ""}`}
                onClick={() => setStrokeWidth(size)}
                disabled={canvasDisabled}
              >
                <span style={{ width: size, height: size, borderRadius: "50%", background: "#fff", display: "inline-block" }} />
              </button>
            ))}
            <button
              className={`picasso-tool-btn ${eraseMode ? "active" : ""}`}
              onClick={toggleEraser}
              disabled={canvasDisabled}
            >
              {t("picasso_eraser")}
            </button>
            <button className="picasso-tool-btn" onClick={() => canvasRef.current?.undo()} disabled={canvasDisabled}>
              <UndoIcon fontSize="small" />
            </button>
            <button className="picasso-tool-btn" onClick={() => canvasRef.current?.redo()} disabled={canvasDisabled}>
              <RedoIcon fontSize="small" />
            </button>
            <button className="picasso-tool-btn" onClick={() => canvasRef.current?.clearCanvas()} disabled={canvasDisabled}>
              <DeleteIcon fontSize="small" />
            </button>
          </div>
        </div>
      </div>

      {hasSubmitted ? (
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8 }}>
          <Typography variant="body2">
            {`${t("submitted")}${submittedCount != null && totalPlayers != null ? ` (${submittedCount}/${totalPlayers})` : "!"}`}
          </Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              setHasSubmitted(false);
              onUnsubmit?.();
            }}
          >
            {t("picasso_unsubmit")}
          </Button>
        </div>
      ) : (
        <Button
          variant="contained"
          color="primary"
          onClick={handleExport}
          disabled={disabled || canvasDisabled || !hasDrawn}
          sx={{ mt: 1 }}
        >
          {`${t("picasso_submit_drawing")}${submittedCount != null && totalPlayers != null ? ` (${submittedCount}/${totalPlayers})` : ""}`}
        </Button>
      )}
    </div>
  );
});

export default PicassoCanvas;
